/* ═══════════════════════════════════════════════════════════
   js/components/jk-card.js — <jk-card> Web Component
   Карточка ЖК: фото, бейджи, избранное, цена, действия
   ═══════════════════════════════════════════════════════════ */
// rating-walkability-fix 1.1.3: единый пол рейтинга. SSOT = window.RATING_FLOOR (shared.js),
// но web-component может грузиться и без shared.js (standalone/SEO) → defensive fallback 60.
// Локальное имя JK_RATING_FLOOR (не RATING_FLOOR), чтобы не реоблъявлять глобал (no-redeclare).
// Значение синхронизировано с PHP RATING_FLOOR (app/config.php) через invariant-тест.
const JK_RATING_FLOOR = (typeof window !== 'undefined' && window.RATING_FLOOR) || 60;

// СТЕНД: адрес обложки приходит в `data-image` СЫРЫМ из базы (`map_buildings.image_url`,
// см. seo_city_type_content.php), а у 37 ЖК там абсолютный `https://bronidom.ru/…`
// (находка 24). На локальной копии сайта это поход на боевой домен, поэтому адрес
// уводится общей стендовой калиткой (js/utils.js). На проде она возвращает адрес
// КАК ЕСТЬ — разметка карточки дословно прежняя. Своего уменьшителя не заводим:
// вторая копия правила и есть находка 30.
const standSrc = (u, w) => (window.standSrc ? window.standSrc(u, w) : u);

class JkCard extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
  }

  static get observedAttributes() {
    return ['data-id', 'data-name', 'data-developer', 'data-district', 'data-floors',
            'data-deadline', 'data-deadline-display', 'data-area', 'data-price-from', 'data-price-m2',
            'data-mortgage', 'data-image', 'data-badges', 'data-fav', 'data-color',
            'data-area-score', 'data-promo-flags', 'data-url'];
  }

  connectedCallback() { this._render(); }
  attributeChangedCallback() { if (this.isConnected) this._render(); }

  get id()        { return this.getAttribute('data-id') || ''; }
  // D.1 (ADR-091): готовый адрес карточки ЖК. Собирать ЧПУ в браузере нельзя —
  // это вторая копия транслита и разбивки витрин. Пусто → jkHref даст старый адрес.
  get url()       { return this.getAttribute('data-url') || ''; }
  get name()      { return this.getAttribute('data-name') || ''; }
  get developer() { return this.getAttribute('data-developer') || ''; }
  get district()  { return this.getAttribute('data-district') || ''; }
  get floors()    { return this.getAttribute('data-floors') || ''; }
  get deadline()  { return this.getAttribute('data-deadline') || ''; }
  get deadlineDisplay() { return this.getAttribute('data-deadline-display') ?? this.deadline; }
  get area()      { return this.getAttribute('data-area') || ''; }
  get priceFrom() { return parseFloat(this.getAttribute('data-price-from')) || 0; }
  get priceM2()   { return parseInt(this.getAttribute('data-price-m2')) || 0; }
  get mortgage()  { return this.getAttribute('data-mortgage') || ''; }
  get image()     { return this.getAttribute('data-image') || ''; }
  get badges()    { return (this.getAttribute('data-badges') || '').split(',').filter(Boolean); }
  get isFav()     { return this.hasAttribute('data-fav'); }
  get color()     { return this.getAttribute('data-color') || '#c8a96e'; }
  get areaScore() { return parseInt(this.getAttribute('data-area-score')) || 0; }
  get promoFlags(){ try { return JSON.parse(this.getAttribute('data-promo-flags') || '{}'); } catch { return {}; } }

  _esc(s) { const d = document.createElement('div'); d.textContent = s; return d.innerHTML; }

  _render() {
    const priceFormatted = this.priceFrom > 0
      ? (this.priceFrom / 1000000).toFixed(1).replace('.', ',') + ' млн \u20BD'
      : 'По запросу';
    const priceM2Formatted = this.priceM2 > 0
      ? this.priceM2.toLocaleString('ru') + ' \u20BD/м\u00B2'
      : '';

    const badgesHtml = this.badges.map(b =>
      '<span class="badge">' + this._esc(b) + '</span>'
    ).join('');

    // Скоринг района badge. F4 (2026-06-09): пол рейтинга ≥60 — консистентно с
    // карточкой ЖК / каталогом / ботом / AI-описанием (0 = нет данных, скрыто).
    const score = this.areaScore > 0 ? Math.max(this.areaScore, JK_RATING_FLOOR) : 0;
    const scoreClass = score >= 70 ? 'high' : (score >= 40 ? 'mid' : 'low');
    const scoreLabel = score >= 70 ? 'Отличный' : (score >= 40 ? 'Средний' : 'Низкий');
    const scoreHtml = score > 0
      ? '<div class="score-row"><span class="score-badge ' + scoreClass + '"><span class="score-icon">\u2605</span> ' + score + '/100 ' + scoreLabel + ' район</span></div>'
      : '';

    // Горячее предложение
    const promos = this.promoFlags;
    const hasPromo = promos && Object.keys(promos).length > 0;
    const hotHtml = hasPromo ? '<div class="hot-badge">\uD83D\uDD25 \u0413\u043E\u0440\u044F\u0447\u0435\u0435</div>' : '';

    const imgHtml = this.image
      ? '<img src="' + this._esc(standSrc(this.image, 640)) + '" alt="\u0416\u041A ' + this._esc(this.name) + '" loading="lazy">'
      : '<div class="placeholder" style="background:' + this.color + '"><span>' + this._esc(this.name.slice(0, 2)) + '</span></div>';

    this.shadowRoot.innerHTML = `
      <style>
        :host { display: block; }
        .card {
          background: var(--dark2, #12121a); border: 1px solid var(--border, rgba(255,255,255,.08));
          border-radius: 16px; overflow: hidden; transition: transform .2s, box-shadow .2s;
        }
        .card:hover { transform: translateY(-4px); box-shadow: 0 12px 40px rgba(0,0,0,.3); }
        .img-wrap { position: relative; height: 200px; overflow: hidden; background: var(--dark3, #1a1a24); }
        .img-wrap img { width: 100%; height: 100%; object-fit: cover; display: block; }
        .placeholder { width: 100%; height: 100%; display: flex; align-items: center; justify-content: center; }
        .placeholder span { font-size: 48px; font-weight: 900; color: #fff; opacity: .5; }
        .badges-row { position: absolute; top: 10px; left: 10px; display: flex; gap: 4px; flex-wrap: wrap; }
        .badge {
          background: rgba(10,10,15,.75); backdrop-filter: blur(6px); color: var(--gold, #c8a96e);
          padding: 3px 10px; border-radius: 6px; font-size: 10px; font-weight: 700;
        }
        .fav-btn {
          position: absolute; top: 10px; right: 10px; background: rgba(10,10,15,.6);
          border: none; color: #8a8a8a; width: 36px; height: 36px; border-radius: 50%;
          cursor: pointer; font-size: 16px; display: flex; align-items: center; justify-content: center;
          transition: all .2s;
        }
        .fav-btn:hover, .fav-btn.active { color: #ef4444; }
        .body { padding: 16px; }
        .district { font-size: 11px; color: var(--text-muted, #8a8a8a); margin-bottom: 4px; }
        .name { font-family: var(--font-display, 'Unbounded', sans-serif); font-size: 16px; font-weight: 800; color: var(--text, #e8e6e1); margin-bottom: 6px; }
        .dev { font-size: 12px; color: var(--text-muted, #8a8a8a); margin-bottom: 12px; }
        .meta { display: flex; gap: 16px; margin-bottom: 12px; }
        .meta-item { text-align: center; }
        .meta-val { display: block; font-weight: 700; font-size: 14px; color: var(--text, #e8e6e1); }
        .meta-lbl { font-size: 10px; color: var(--text-muted, #8a8a8a); }
        .price-row { display: flex; align-items: flex-end; justify-content: space-between; margin-bottom: 12px; }
        .price-from { font-size: 10px; color: var(--text-muted, #8a8a8a); }
        .price { font-family: var(--font-display, 'Unbounded', sans-serif); font-size: 20px; font-weight: 900; color: var(--gold, #c8a96e); }
        .price-m2 { font-size: 11px; color: var(--text-muted, #8a8a8a); }
        .mortgage-badge { font-size: 12px; color: var(--gold, #c8a96e); }
        .actions { display: flex; gap: 8px; }
        .btn-primary {
          flex: 1; background: linear-gradient(135deg, var(--gold-dark, #b08d4e), var(--gold-light, #d4b978));
          color: var(--dark, #0a0a0f); border: none; padding: 10px 16px; border-radius: 10px;
          font-weight: 700; font-size: 13px; cursor: pointer; transition: transform .15s;
        }
        .btn-primary:hover { transform: translateY(-1px); }
        .btn-outline {
          background: none; border: 1px solid var(--border, rgba(255,255,255,.08));
          color: var(--text-muted, #8a8a8a); width: 40px; height: 40px; border-radius: 10px;
          cursor: pointer; display: flex; align-items: center; justify-content: center;
          font-size: 14px; transition: all .2s; text-decoration: none;
        }
        .btn-outline:hover { border-color: var(--gold, #c8a96e); color: var(--gold, #c8a96e); }
        .score-badge {
          display: inline-flex; align-items: center; gap: 4px;
          font-size: 11px; font-weight: 700; padding: 3px 8px;
          border-radius: 6px; color: #fff;
        }
        .score-badge.high { background: #22c55e; }
        .score-badge.mid  { background: #eab308; color: #1a1a24; }
        .score-badge.low  { background: #ef4444; }
        .score-icon { font-size: 12px; }
        .hot-badge {
          position: absolute; top: 10px; right: 52px;
          background: linear-gradient(135deg, #ef4444, #f97316);
          color: #fff; font-size: 10px; font-weight: 800; padding: 4px 10px;
          border-radius: 6px; text-transform: uppercase; letter-spacing: 0.5px;
          animation: hotPulse 2s ease-in-out infinite;
        }
        @keyframes hotPulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(239,68,68,.4); }
          50% { box-shadow: 0 0 0 6px rgba(239,68,68,0); }
        }
        .score-row { display: flex; align-items: center; gap: 8px; margin-bottom: 8px; }
      </style>
      <div class="card" part="card">
        <div class="img-wrap">
          ${imgHtml}
          <div class="badges-row">${badgesHtml}</div>
          ${hotHtml}
          <button class="fav-btn ${this.isFav ? 'active' : ''}" aria-label="В избранное">${this.isFav ? '\u2764' : '\u2661'}</button>
        </div>
        <div class="body">
          <div class="district">${this.district ? this._esc(this.district) + ' район' : ''}</div>
          <div class="name">\u0416\u041A \u00AB${this._esc(this.name)}\u00BB</div>
          <div class="dev">${this._esc(this.developer)}</div>
          <div class="meta">
            <div class="meta-item"><span class="meta-val">${this._esc(this.floors)}</span><span class="meta-lbl">Этажей</span></div>
            <div class="meta-item"><span class="meta-val">${this._esc(this.deadlineDisplay)}</span><span class="meta-lbl">Сдача</span></div>
            <div class="meta-item"><span class="meta-val">${this._esc(this.area)}</span><span class="meta-lbl">Площадь</span></div>
          </div>
          ${scoreHtml}
          <div class="price-row">
            <div>
              <div class="price-from">Цена от</div>
              <div class="price">${priceFormatted}</div>
              <div class="price-m2">${priceM2Formatted}</div>
            </div>
            ${this.mortgage ? '<div class="mortgage-badge">' + this._esc(this.mortgage) + '</div>' : ''}
          </div>
          <div class="actions">
            <button class="btn-primary" data-action="detail">Подробнее</button>
            <a href="${this._esc(jkHref({ id: this.id, url: this.url }))}" class="btn-outline" target="_blank" title="Страница ЖК">\u2197</a>
            <button class="btn-outline" data-action="booking" title="Записаться на показ">\uD83D\uDCC5</button>
          </div>
        </div>
      </div>
    `;

    // Events
    this.shadowRoot.querySelector('.fav-btn').addEventListener('click', () => this.toggleFav());
    this.shadowRoot.querySelector('[data-action="detail"]').addEventListener('click', () => {
      this.dispatchEvent(new CustomEvent('jk-card:detail', { bubbles: true, detail: { id: this.id } }));
    });
    const bookBtn = this.shadowRoot.querySelector('[data-action="booking"]');
    if (bookBtn) {
      bookBtn.addEventListener('click', () => {
        this.dispatchEvent(new CustomEvent('jk-card:booking', { bubbles: true, detail: { id: this.id, name: this.name } }));
      });
    }
  }

  toggleFav() {
    if (this.isFav) {
      this.removeAttribute('data-fav');
    } else {
      this.setAttribute('data-fav', '');
    }
    this.dispatchEvent(new CustomEvent('jk-card:fav', {
      bubbles: true,
      detail: { id: this.id, fav: this.isFav }
    }));
  }

  setFav(val) {
    if (val) this.setAttribute('data-fav', '');
    else this.removeAttribute('data-fav');
  }
}

if (!customElements.get('jk-card')) {
  customElements.define('jk-card', JkCard);
}
